import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { UserService } from './user.service';
import { ApiService } from './api.service';

/**
* Service for interacting with Standard/Compliance report endpoints and managing transient report state (PDF name and compliance flag).
* @component
* @selector app-standard-report-service
* @example
*   <app-standard-report-service [params]="payload" [token]="authToken"></app-standard-report-service>
* @input any params - Request payload used by the service's API methods (filters, paging, report identifiers and options).
* @input string token - Optional bearer token used by weekly-report flows when a token is generated externally or passed through a different flow.
* @output Observable<HttpResponse<any>> getLocationDropdownData - Emits HTTP response containing dropdown/entity list data for report binding.
* @output Observable<HttpResponse<any>> getGridData - Emits HTTP response containing Kendo grid report data.
* @output Observable<HttpResponse<Blob>> downloadRequestedReport - Emits Blob HTTP response for export requests (downloadable file).
* @output Observable<HttpResponse<Blob>> downloadReport - Emits Blob HTTP response for compliance standard report downloads.
* @output Observable<HttpResponse<Blob>> viewReport - Emits Blob HTTP response for viewing reports inline.
* @output Observable<HttpResponse<any>> getAuthorisedReportData - Emits authorised report data used in approval/authorisation workflows.
* @output Observable<HttpResponse<any>> getWeeklyReportData - Emits weekly report details when provided with an explicit token.
* @output Observable<HttpResponse<any>> generateTokenByUserIdForWeeklyReport - Emits token generation response for weekly-report user switching.
* @output Observable<HttpResponse<Blob>> downloadWeeklyReport - Emits Blob HTTP response for downloading weekly reports (requires token).
* @output Observable<HttpResponse<any>> checkWeeklyStandardReportRedirection - Emits validation/redirection result for weekly-report access checks.
* @output Observable<HttpResponse<any>> sendOtpToGetAccessToWeeklyReport - Emits OTP generation response used for user-switcher access to weekly reports.
* @property {string | null} pdfName - In-memory filename for the currently requested PDF; managed via setPdfName/getPdfName/clearPdfName.
* @property {string | null} complianceFlag - In-memory flag to track compliance state across flows; managed via setComplianceFlag/getComplianceFlag/clearComplianceFlag.
* @method getLocationDropdownData(params: any): Observable<HttpResponse<any>> - Retrieves entity/location dropdown data; ensures a valid token via UserService and signs payload with HMAC header.
* @method getGridData(params: any): Observable<HttpResponse<any>> - Obtains grid (Kendo) report data; automatically refreshes token and attaches HMAC and auth headers.
* @method downloadRequestedReport(params: any): Observable<HttpResponse<Blob>> - Requests an exported report file and returns a Blob response suitable for download; uses token + HMAC signing.
* @method downloadReport(params: any): Observable<HttpResponse<Blob>> - Downloads a compliance standard report as a Blob with proper auth and HMAC headers.
* @method viewReport(params: any): Observable<HttpResponse<Blob>> - Retrieves a report as a Blob for viewing (inline or preview).
* @method getAuthorisedReportData(params: any): Observable<HttpResponse<any>> - Fetches report data necessary for authorisation workflows (uses token + HMAC).
* @method setPdfName(name: string): void - Stores a PDF filename in memory for later retrieval or download naming.
* @method getPdfName(): string | null - Returns the stored PDF filename or null if none set.
* @method clearPdfName(): void - Clears the stored PDF filename.
* @method setComplianceFlag(flag: string): void - Stores a compliance-related flag in memory.
* @method getComplianceFlag(): string | null - Returns the stored compliance flag or null if none set.
* @method clearComplianceFlag(): void - Clears the stored compliance flag.
* @method getWeeklyReportData(params: any, token: string): Observable<HttpResponse<any>> - Retrieves weekly report details using an explicitly provided token and HMAC signing (used by delegated/weekly flows).
* @method generateTokenByUserIdForWeeklyReport(params: any): Observable<HttpResponse<any>> - Requests generation of a token for user-switcher / weekly-report access.
* @method downloadWeeklyReport(params: any, token: string): Observable<HttpResponse<Blob>> - Downloads the weekly report using a provided token; returns Blob response for file download.
* @method checkWeeklyStandardReportRedirection(params: any, token: string): Observable<HttpResponse<any>> - Checks redirection/permission state for weekly reports using provided token and HMAC.
* @method sendOtpToGetAccessToWeeklyReport(params: any, token: string): Observable<HttpResponse<any>> - Initiates OTP generation for user-switcher flows to grant access to weekly reports.
* Lifecycle: Provided in root (singleton). No Angular template lifecycle hooks apply; methods typically call UserService.ensureValidToken() to acquire/refresh a bearer token, then generate HMAC via UserService.generateHmac(params, token) and attach appropriate headers before making HttpClient requests.
*/
@Injectable({
  providedIn: 'root',
})
export class StandardReportService {

  private readonly gridDataCache = new Map<string, any[]>();

  constructor(private http: HttpClient, private user: UserService, private api: ApiService) { }

  /**
   * Stores transformed grid rows (with Date objects) for a given compliance type and report flag.
   * @param complianceType - 'S' or 'I'
   * @param reportFlag - e.g. 'ComprehensiveReport'
   * @param data - transformed row array to cache
   */
  cacheGridData(complianceType: string, reportFlag: string, data: any[]): void {
    this.gridDataCache.set(`${complianceType}:${reportFlag}`, data);
  }

  /**
   * Returns cached grid rows for the given key, or null when the data has not been fetched yet
   * or was explicitly invalidated (e.g. after generating a new report).
   */
  getCachedGridData(complianceType: string, reportFlag: string): any[] | null {
    return this.gridDataCache.get(`${complianceType}:${reportFlag}`) ?? null;
  }

  /**
   * Removes the cached entry for a single report type and compliance type.
   * Use this before navigating to the report viewer so only that card re-fetches
   * on return (instead of all cards re-fetching after a full cache wipe).
   */
  invalidateGridDataEntry(complianceType: string, reportFlag: string): void {
    this.gridDataCache.delete(`${complianceType}:${reportFlag}`);
  }

  /** Clears all cached grid data — use only when a full refresh of every card is needed. */
  invalidateGridDataCache(): void {
    this.gridDataCache.clear();
  }

  /**
  * Fetches ET location dropdown data by ensuring a valid token, signing the payload with an HMAC, and POSTing to the standard-report entity list endpoint.
  * @example
  * etLocationDropdownData({ orgId: 123, filter: 'active' })
  * Observable<HttpResponse<any>>
  * @param {{any}} {{params}} - Request payload object used as the POST body and to generate the X-Payload-HMAC header.
  * @returns {{Observable<HttpResponse<any>>}} Observable emitting the full HTTP response containing the dropdown data.
  **/
  getLocationDropdownData(params: any): Observable<HttpResponse<any>> {
    const url = `${this.user.apiBaseUrl}/dropdown/bind-standard-report-entity-list `;
    return this.api.post(url, params);
  }

  /**
  * Request grid/report data from the backend with HMAC-authenticated headers and return the full HTTP response as an Observable.
  * @example
  * etGridData({ page: 1, pageSize: 20 })
  * Observable<HttpResponse<any>>
  * @param {{any}} {{params}} - Request payload and query parameters sent to the report endpoint.
  * @returns {{Observable<HttpResponse<any>>}} An Observable that emits the full HttpResponse containing the report/grid data.
  **/
  getGridData(params: any): Observable<HttpResponse<any>> {
    const url = `${this.user.apiBaseUrl}/report/kendo-my-report-data`;
    return this.api.post(url, params);
  }

  /**
  * Initiates a request to export a detailed report and returns the HTTP response containing the report as a Blob.
  * @example
  * ondownloadRequestedReport({ reportId: 123, filter: 'all' })
  * Observable<HttpResponse<Blob>>
  * @param {{any}} params - Request payload or filter/options used to generate the report.
  * @returns {{Observable<HttpResponse<Blob>>}} Observable that emits the full HTTP response containing the report Blob.
  **/
  downloadRequestedReport(params: any): Observable<HttpResponse<Blob>> {
    const url = `${this.user.apiBaseUrl}/report/export-report-request-detailed`;
    return this.api.downloadBlob(url, params);
  }

  /**
  * Download a compliance standard report from the API and return the HTTP response containing the Blob.
  * @example
  * downloadReport({ reportId: '1234' })
  * Observable<HttpResponse<Blob>> that emits the HTTP response with the report blob
  * @param {{any}} {{params}} - Parameters object sent in the POST body to request the report (used to build HMAC).
  * @returns {{Observable<HttpResponse<Blob>>}} Observable that emits the HttpResponse wrapping the report Blob.
  **/
  downloadReport(params: any): Observable<HttpResponse<Blob>> {
    const url = `${this.user.apiBaseUrl}/report/download-compliance-standard-report`;
    return this.api.downloadBlob(url, params);
  }

  /**
  * View a compliance standard report as a Blob by posting the provided params to the backend. The method ensures a valid token, generates an HMAC for the payload, sets the appropriate headers, and returns the full HTTP response containing the report as a Blob.
  * @example
  * iewReport({ standardId: '123' })
  * Observable<HttpResponse<Blob>>
  * @param {{any}} {{params}} - Request payload sent to the /report/view-compliance-standard-report endpoint; used to generate the X-Payload-HMAC header.
  * @returns {{Observable<HttpResponse<Blob>>}} Observable emitting the full HttpResponse that contains the report Blob.
  **/
  viewReport(params: any): Observable<HttpResponse<Blob>> {
    const url = `${this.user.apiBaseUrl}/report/view-compliance-standard-report`;
    return this.api.downloadBlob(url, params);
  }

  /**
  * Send an authorised POST to retrieve report data, ensuring a valid token and HMAC header.
  * @example
  * etAuthorisedReportData({ reportId: '123', filters: { from: '2020-01-01' } })
  * Observable<HttpResponse<any>>
  * @param {any} params - Parameters object sent as the POST body when requesting report data.
  * @returns {Observable<HttpResponse<any>>} Observable that emits the full HTTP response containing the report data.
  **/
  getAuthorisedReportData(params: any): Observable<HttpResponse<any>> {
    const url = `${this.user.apiBaseUrl}/report/authorise-get-report-data-n`;
    return this.api.post(url, params);
  }

  private pdfName: string | null = null;

  setPdfName(name: string) {
    this.pdfName = name;
  }

  getPdfName(): string | null {
    return this.pdfName;
  }

  clearPdfName() {
    this.pdfName = null;
  }

  private complianceFlag: string | null = null;

  setComplianceFlag(flag: string) {
    this.complianceFlag = flag;
  }

  getComplianceFlag(): string | null {
    return this.complianceFlag;
  }

  clearComplianceFlag() {
    this.complianceFlag = null;
  }

  /**
  * Send a POST request to retrieve standard report details with HMAC-based authorization.
  * @example
  * etWeeklyReportData({ reportId: 123, filter: {} }, 'token123')
  * Observable<HttpResponse<any>>
  * @param {{any}} {{params}} - Request payload parameters sent in the POST body.
  * @param {{string}} {{token}} - Bearer token used for Authorization header and HMAC generation.
  * @returns {{Observable<HttpResponse<any>>}} Observable resolving to the full HTTP response containing the report details.
  **/
  getWeeklyReportData(params: any, token: string): Observable<HttpResponse<any>> {
    const url = `${this.user.apiBaseUrl}/report/get-standard-report-details`;

    const hmac = this.user.generateHmac(params, token);

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-Payload-HMAC': hmac,
    });

    return this.http.post<any>(url, params, {
      headers: headers,
      observe: 'response'
    });
  }

  generateTokenByUserIdForWeeklyReport(params: any): Observable<HttpResponse<any>> {
    const url = `${this.user.apiBaseUrl}/token/generate-token-by-userid`;
    return this.http.post<any>(url, params, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      observe: 'response'
    });
  }

  /**
  * Downloads the weekly standard report as a Blob wrapped in an HttpResponse Observable.
  * @example
  * downloadWeeklyReport({ startDate: '2026-01-01', endDate: '2026-01-07' }, 'eyJhbGci...')
  * Observable<HttpResponse<Blob>> (emits an HttpResponse whose body is the report blob)
  * @param {{any}} {{params}} - Request payload containing report parameters (e.g., date range, filters).
  * @param {{string}} {{token}} - Bearer token used for authorization and HMAC generation.
  * @returns {{Observable<HttpResponse<Blob>>}} Return an Observable that resolves to an HttpResponse containing the report Blob.
  **/
  downloadWeeklyReport(params: any, token: string): Observable<HttpResponse<Blob>> {
    const url = `${this.user.apiBaseUrl}/report/download-standard-report`;
    const hmac = this.user.generateHmac(params, token);
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-Payload-HMAC': hmac,
    });

    return this.http.post<Blob>(url, params, {
      headers: headers,
      observe: 'response',
      responseType: 'blob' as 'json'
    });
  }

  /**
  * Check whether weekly standard report should redirect by posting the given params to the API with HMAC and Bearer authorization.
  * @example
  * heckWeeklyStandardReportRedirection({ reportId: 123 }, 'eyJhbGciOi...') 
  * // Returns Observable<HttpResponse<any>>
  * @param {any} params - Request payload sent to the check-standard-report-redirection endpoint.
  * @param {string} token - Bearer token used for the Authorization header and to generate the X-Payload-HMAC.
  * @returns {Observable<HttpResponse<any>>} Observable emitting the full HTTP response from the API.
  **/
  checkWeeklyStandardReportRedirection(params: any, token: string): Observable<HttpResponse<any>> {
    const url = `${this.user.apiBaseUrl}/login/check-standard-report-redirection`;
    const hmac = this.user.generateHmac(params, token);
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-Payload-HMAC': hmac,
    });
    return this.http.post<any>(url, params, {
      headers: headers,
      observe: 'response'
    });
  }

  /**
  * Generate an OTP to obtain access to the weekly report by posting the provided payload and HMAC-authenticating the request.
  * @example
  * endOtpToGetAccessToWeeklyReport({ reportId: 123 }, 'token123')
  * Observable<HttpResponse<any>>
  * @param {{any}} {{params}} - Request payload sent to the OTP generation endpoint; used to compute the X-Payload-HMAC header.
  * @param {{string}} {{token}} - Bearer token used for the Authorization header and included in HMAC generation.
  * @returns {{Observable<HttpResponse<any>>}} Observable<HttpResponse<any>> containing the full HTTP response from the OTP generation endpoint.
  **/
  sendOtpToGetAccessToWeeklyReport(params: any, token: string): Observable<HttpResponse<any>> {
    const url = `${this.user.apiBaseUrl}/user-switcher/generate-otp-user-switcher`;
    const hmac = this.user.generateHmac(params, token);

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-Payload-HMAC': hmac,
    });

    return this.http.post<any>(url, params, {
      headers: headers,
      observe: 'response'
    });
  }

  /**
   * Re-sends the OTP for the weekly report's own access flow.
   *
   * The token is passed in rather than taken from `UserService`, because this
   * flow runs before a normal session exists — it is the short-lived token
   * issued by the weekly report auth redirect, and it signs the HMAC as well as
   * authorising the request.
   * @param params - Payload identifying the recipient of the OTP.
   * @param token - The weekly-report access token used for both bearer auth and HMAC signing.
   * @returns Observable emitting the full HTTP response from the resend request.
   */
  resendOTPToGetAccessToWeeklyReport(params: any, token: string): Observable<HttpResponse<any>> {
    const url = `${environment.API_BASE_URL}/login/resend-otp`;
    const hmac = this.user.generateHmac(params, token);

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-Payload-HMAC': hmac,
    });

    return this.http.post<any>(url, params, {
      headers: headers,
      observe: 'response'
    });
  }


  /**
   * Verifies the one-time password and returns the HTTP response for accessing the weekly report.
   * @example
   * verifyOTPToGetAccessToWeeklyReport(params, token)
   * HttpResponse<any>
   * @param {any} params - Request payload containing the OTP verification data.
   * @param {string} token - Bearer token used for authorization and HMAC generation.
   * @returns {Observable<HttpResponse<any>>} Observable emitting the HTTP response from the OTP verification request.
   */
  verifyOTPToGetAccessToWeeklyReport(params: any, token: string): Observable<HttpResponse<any>> {
    const url = `${environment.API_BASE_URL}/login/verifyotp`;
    const hmac = this.user.generateHmac(params, token);

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-Payload-HMAC': hmac,
    });

    return this.http.post<any>(url, params, {
      headers: headers,
      observe: 'response'
    });
  }

}

